import type { ReactNode } from 'react'
import { cx } from '../cx'
import { Icon, type IconName } from '../Icon'

export type BadgeTone =
  | 'neutral'
  | 'accent'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'premium'

export function Badge({
  tone = 'neutral',
  outline,
  icon,
  dot,
  className,
  children,
  ...rest
}: {
  tone?: BadgeTone
  outline?: boolean
  icon?: IconName
  dot?: boolean
  className?: string
  children: ReactNode
} & React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={cx('badge', outline ? 'badge-outline' : `badge-${tone}`, className)} {...rest}>
      {dot && <span className="dot dot-live" />}
      {icon && <Icon name={icon} />}
      {children}
    </span>
  )
}

export type GradeKey = 'gold' | 'silver' | 'bronze' | 'none'

const GRADE_LABEL: Record<GradeKey, string> = {
  gold: 'طلایی',
  silver: 'نقره‌ای',
  bronze: 'برنزی',
  none: 'بدون گرید',
}

/**
 * The grade as a word. The score number is not part of the chip: the word is
 * the signal, and a screen that needs the score (the grade page) prints it
 * on its own.
 */
export function GradeChip({
  grade,
  size = 'md',
  className,
}: {
  grade: GradeKey
  /** @deprecated Ignored. The chip shows the grade word only. */
  score?: number
  size?: 'sm' | 'md'
  className?: string
}) {
  const small = size === 'sm'
  return (
    <span
      className={cx('grade', `grade-${grade}`, className)}
      style={small ? { blockSize: 16, paddingInline: 5, fontSize: 10 } : undefined}
    >
      <Icon name="medal" style={small ? { inlineSize: 10, blockSize: 10 } : undefined} />
      {GRADE_LABEL[grade]}
    </span>
  )
}

export type MetalKey = 'copper' | 'brass' | 'zinc' | 'alu' | 'lead' | 'tin'

export function MetalChip({
  metal,
  label,
  className,
}: {
  metal: MetalKey
  label: string
  className?: string
}) {
  return (
    <span className={cx('chip-metal', className)}>
      <span className="swatch" style={{ background: `var(--p-metal-${metal})` }} />
      {label}
    </span>
  )
}

export function Dot({
  tone = 'live',
  pulse,
  className,
}: {
  tone?: 'live' | 'buy' | 'sell' | 'muted'
  pulse?: boolean
  className?: string
}) {
  const style =
    tone === 'buy'
      ? { background: 'var(--c-buy)' }
      : tone === 'sell'
        ? { background: 'var(--c-sell)' }
        : tone === 'muted'
          ? { background: 'var(--c-fg-disabled)' }
          : undefined
  return (
    <span
      className={cx('dot', tone === 'live' && 'dot-live', pulse && 'pulse', className)}
      style={style}
    />
  )
}

export function Avatar({
  name,
  size = 36,
  icon,
  tone = 'default',
  className,
}: {
  name: string | null | undefined
  size?: 24 | 30 | 32 | 36 | 48
  icon?: IconName
  tone?: 'default' | 'danger' | 'dark'
  className?: string
}) {
  const style: React.CSSProperties = {
    inlineSize: size,
    blockSize: size,
    fontSize: size <= 30 ? 12 : size <= 36 ? 13 : 15,
  }
  if (tone === 'danger') {
    style.background = 'var(--c-danger-soft)'
    style.color = 'var(--c-danger)'
    style.borderColor = 'var(--c-danger-soft)'
  }
  return (
    <span
      className={cx('avatar', size >= 48 && 'avatar-lg', tone === 'dark' && 'avatar-dark', className)}
      style={style}
      aria-hidden="true"
    >
      {icon ? <Icon name={icon} size="sm" /> : firstLetter(name)}
    </span>
  )
}

export interface PartyProps {
  /**
   * Null while the counterparty is masked, before the commission is paid.
   * Undefined reads the same way: a party the screen cannot name yet is drawn
   * by its role word under a lock, never by a blank and never by a number.
   * Where a list needs to tell masked parties apart, the order number goes
   * in `meta` as a muted second line, never into the name.
   */
  name: string | null | undefined
  /**
   * The masked label, already worded by the server. Where a route sends one,
   * it wins: the wording of a mask is the server's and not this module's.
   */
  maskedFa?: string | null
  side?: 'buyer' | 'seller'
  meta?: ReactNode
  grade?: GradeKey
  /** @deprecated Ignored. The grade chip shows the grade word only. */
  score?: number
  size?: 24 | 30 | 32 | 36 | 48
  className?: string
}

export function Party({
  name,
  maskedFa,
  side,
  meta,
  grade,
  size = 36,
  className,
}: PartyProps) {
  const masked = name === null || name === undefined
  const label = masked
    ? (maskedFa ?? (side === 'buyer' ? 'خریدار' : side === 'seller' ? 'فروشنده' : 'طرف معامله'))
    : name
  return (
    <span className={cx('party', className)}>
      <Avatar name={masked ? '' : label} size={size} icon={masked ? 'lock' : undefined} />
      <span className="col" style={{ gap: 2 }}>
        <span className="p-name">{label}</span>
        <span className="p-meta">
          {grade && <GradeChip grade={grade} size="sm" />}
          {meta}
        </span>
      </span>
    </span>
  )
}

export function Serial({ children, className }: { children: string; className?: string }) {
  return <span className={cx('serial', className)}>{children}</span>
}

function firstLetter(name: string | null | undefined): string {
  const trimmed = name?.trim()
  if (!trimmed) return ''
  return [...trimmed][0] ?? ''
}

/**
 * The rule behind a system decision, named on the row it decided: «قاعده ۳:
 * قیمت برابر، زمان زودتر»، «خارج از باند قیمت»، «شماره تماس». The operator
 * confirms a reason, not a number. `tone` marks a rule that excluded or
 * withheld something (warning) apart from one that only ranked (neutral).
 */
export function RuleTag({
  tone = 'neutral',
  title,
  className,
  children,
}: {
  tone?: 'neutral' | 'warning' | 'danger'
  title?: string
  className?: string
  children: ReactNode
}) {
  return (
    <span className={cx('badge rule-tag', `rule-tag-${tone}`, className)} title={title}>
      <Icon name="scale" />
      {children}
    </span>
  )
}
